import { useEffect, useState, useRef } from "react";
import { useOutletContext } from "react-router";
import styled from "styled-components";
import { AppQueryClient } from "../../../api/queryClient";
import { APP_TITLE } from "../../../config/constant";
import { defaultStatusColors } from "../../../utils/CardUtils";
import * as Library from "../../../utils/Library";
import InfinityGridPaymentMethods from "../../common/cardContainers/InfinityGridPaymentMethods";
import DialogBoxComponent from "../../common/dialogs/DialogBoxComponent";
import ListComponent from "../../common/lists/ListComponent";
import MainWindowHeader from "../../common/mainApp/mainWindowHeaders/MainWindowHeader";
import FullScreenOverlay from "../../common/overlay/FullScreenOverlay";

// #region  SCOPED CSS
const StyledPage = styled.div`
    & {
        overflow-y: hidden;
        height: 100%;
        margin-right: -23px;
        margin-left: -24px;
        padding-left: 24px;
        padding-right: 24px;
    }
`;
// #endregion

export default function PaymentMethods() {
    document.title = `${APP_TITLE} - Payment Methods`;

    // #region VARS
    const { MainLayoutProps, layoutType } = useOutletContext();
    const [viewLayoutStyle, setViewLayoutStyle] = useState("grid");
    const props = {
        layoutType: layoutType,
        setControlBarLeftBtns: MainLayoutProps?.setControlBarLeftBtns,
        setControlBarCenterBtns: MainLayoutProps?.setControlBarCenterBtns,
        setControlBarRightBtns: MainLayoutProps?.setControlBarRightBtns,
        setFilterHeaderProperties: MainLayoutProps?.setFilterHeaderProperties,
        setRefreshUserData: MainLayoutProps?.setRefreshUserData
    };

    const [paymentMethodToDelete, setPaymentMethodToDelete] = useState({
        paymentMethodID: null,
        paymentMethodCardNr: null
    });
    const [deletePopup, setDeletePopup] = useState({
        isOpen: false,
        isLoading: false
    });
    //-----
    const [paymentMethodToMakePrimary, setPaymentMethodToMakePrimary] = useState({
        paymentMethodID: null,
        paymentMethodCardNr: null
    });
    const [makePrimaryPopup, setMakePrimaryPopup] = useState({
        isOpen: false,
        isLoading: false
    });

    //-----
    const [searchText, setSearchText] = useState("");
    const [listNumRows, setListNumRows] = useState(20);
    const filters = { searchText: searchText };
    const infiniteGridQuerykey = ["PaymentMethodsGrid", filters];
    const listQueryKey = ["PaymentMethodsList", filters];
    const gridRef = useRef(null);
    // #endregion

    // #region METHODS
    const onClickRefreshAction = () => {
        if (props?.layoutType === "List") AppQueryClient.invalidateQueries(listQueryKey);
        else {
            //else AppQueryClient.invalidateQueries(infiniteGridQuerykey);
            gridRef.current?.reset?.(infiniteGridQuerykey);
        }
    };

    const handleDeletepopupConfiormation = (paymentMethodID, paymentMethodCardNr) => {
        setPaymentMethodToDelete({ paymentMethodID, paymentMethodCardNr });
        setDeletePopup({ isOpen: true, isLoading: false });
    };

    const onDeleteAction = paymentMethodID => {
        setDeletePopup(preState => ({ ...preState, isLoading: true }));
        Library.makeApiRequest({
            endpoint: "Global/deletePaymentMethod",
            data: { paymentMethodID: paymentMethodID }
        })
            .then(res => {
                if (res?.status === 200) {
                    Library.showToastMessage({
                        title: "Payment method removed!",
                        message: `Your Amex card ending ${paymentMethodToDelete?.paymentMethodCardNr} was removed.`
                    });
                }
            })
            .catch(error => {
                //...
            })
            .finally(() => {
                setDeletePopup({ isOpen: false, isLoading: false });
                onClickRefreshAction();
            });
    };

    const handleMakePrimaryPopupConfiormation = (paymentMethodID, paymentMethodCardNr) => {
        setPaymentMethodToMakePrimary({ paymentMethodID, paymentMethodCardNr });
        setMakePrimaryPopup({ isOpen: true, isLoading: false });
    };

    const onMakePrimaryAction = paymentMethodID => {
        setMakePrimaryPopup(preState => ({ ...preState, isLoading: true }));
        Library.makeApiRequest({
            endpoint: "Global/updateDefaultPaymentMethod",
            data: { paymentMethodID: paymentMethodID }
        })
            .then(res => {
                if (res?.status === 200) {
                    Library.showToastMessage({
                        title: "Payment method updated!",
                        message: "Your payment method was set as primary."
                    });
                }
            })
            .catch(error => {
                //...
            })
            .finally(() => {
                setMakePrimaryPopup({ isOpen: false, isLoading: false });
                onClickRefreshAction();
            });
    };

    const onSearch = searchText => {
        setSearchText(searchText);
    };

    // #endregion

    // #region EFFECTS
    useEffect(() => {
        setViewLayoutStyle(props?.layoutType);
        setupHeaders(props, onClickRefreshAction, onSearch);
    }, [props?.layoutType]);

    // #endregion

    // #region LIST COMPONENT PROPS
    const listColumns = [
        {
            name: "ISSUER",
            selector: row => row?.stripeCardIssuer ?? "--"
        },
        {
            name: "BOTAG ID",
            selector: row => row?.ourID ?? "--"
        },
        {
            name: "Card nr",
            selector: row => row?.stripeCardNumber ?? "--"
        },
        {
            name: "Expiration",
            selector: row => row?.stripeCardExpDate ?? "--"
        },
        {
            name: "CVC",
            selector: row => "***"
        },
        {
            name: "Country",
            selector: row => "Norway"
        }
    ];
    // #endregion

    // #region RENDER
    return (
        <>
            <FullScreenOverlay show={deletePopup.isOpen}>
                <DialogBoxComponent
                    title="Remove payment method?"
                    message={`This card ending with ${paymentMethodToDelete?.paymentMethodCardNr} will be removed.`}
                    heading="Just checking..."
                    onClickYes={() => {
                        onDeleteAction(paymentMethodToDelete?.paymentMethodID);
                    }}
                    onClickNo={() => {
                        setDeletePopup({ isOpen: false, isLoading: false });
                    }}
                    confirmState="secondary"
                    loading={deletePopup.isLoading}
                />
            </FullScreenOverlay>
            <FullScreenOverlay show={makePrimaryPopup.isOpen}>
                <DialogBoxComponent
                    title="Make this payment method primary?"
                    message={`This card ending with ${paymentMethodToMakePrimary?.paymentMethodCardNr} will be set as primary.`}
                    heading="Just checking..."
                    onClickYes={() => {
                        onMakePrimaryAction(paymentMethodToMakePrimary?.paymentMethodID);
                    }}
                    onClickNo={() => {
                        setMakePrimaryPopup({ isOpen: false, isLoading: false });
                    }}
                    confirmState="secondary"
                    loading={makePrimaryPopup.isLoading}
                />
            </FullScreenOverlay>
            <StyledPage>
                {viewLayoutStyle === "Grid" && (
                    <InfinityGridPaymentMethods
                        ref={gridRef}
                        minColumnWidth="238px"
                        defaultSecondTitle="Account:"
                        limit={10}
                        queryKey={infiniteGridQuerykey}
                        statusFromPath="status"
                        filters={filters}
                        onDeleteAction={handleDeletepopupConfiormation}
                        onPrimaryAction={handleMakePrimaryPopupConfiormation}
                    />
                )}

                {viewLayoutStyle === "List" && (
                    <ListComponent
                        title="Payment Methods"
                        columns={listColumns}
                        api={{
                            useCommonPostRequest: true,
                            commonContext: "Global",
                            endpoint: "getPaymentMethods",
                            formData: {
                                companyID: localStorage.getItem("currentCompanyID"),
                                limit: listNumRows,
                                searchText: filters?.searchText || "",
                                ...filters
                            }
                            //onSuccess: mapListData
                        }}
                        pageLimit={listNumRows}
                        currentPage={1}
                        queryKey={listQueryKey}
                        actions={{
                            includeActions: true,
                            // onClickEdit: onEdit,
                            onClickRemove: onDeleteAction,
                            // onClickSuspend: onSuspendAction,
                            extraActions: [{ title: "MAKE PRIMARY", onClick: onMakePrimaryAction }]
                        }}
                        //TODO: Add this new prop to the list os props
                        actionsrestrictions={{
                            editAccesseLowerRoleOnly: true,
                            removeAccesseLowerRoleOnly: true,
                            suspendAccesseLowerRoleOnly: true,
                            onlyEditHisOwnUser: true
                        }}
                        includeStatus={{
                            stickyStatus: true,
                            statusFromPath: "status",
                            statusColors: defaultStatusColors
                        }}
                        onChangeRowsPerPage={rowsPerPage => setListNumRows(rowsPerPage)}
                    />
                )}
            </StyledPage>
        </>
    );
    // #endregion
}

// #region HEADER SETUP
function setupHeaders(props, onClickRefreshAction, onSearch) {
    props.setControlBarLeftBtns([{ to: "#", disabled: true, title: "Invoices", icon: "fa-magnifying-glass" }]);
    props.setControlBarCenterBtns([{ to: "/billing", active: true, disabled: false, title: "BILLING" }]);
    props.setControlBarRightBtns([
        { to: "/billing", active: false, disabled: false, title: "Plans", icon: "file-check-01" },
        { to: "/billing/invoices", active: false, disabled: false, title: "Invoices", icon: "file-check-02" },
        { to: "/billing/reports", active: false, disabled: true, title: "Subs", icon: "file-03" },
        { to: "/billing/methods", active: true, disabled: false, title: "Methods", icon: "credit-card-check" }
    ]);

    props.setFilterHeaderProperties({
        customContentHeader: (
            <MainWindowHeader
                key={window.location.pathname}
                visible={true}
                layoutTypeOptions={{
                    gridEnabled: true,
                    listEnabled: true
                }}
                onRefreshClick={onClickRefreshAction}
                firstOrganize={{
                    visible: true,
                    dropdownData: [{ name: "none", value: "none" }],
                    // onDropdownChange: onLabelBrandChange,
                    disabled: true,
                    type: "singleSelect",
                    selectedValue: "none"
                }}
                secondOrganize={{
                    visible: true,
                    disabled: true
                }}
                pageFilters={{
                    useFilters: true,
                    filters: {
                        useLabelBrands: true,
                        useRetailBrands: true
                    }
                    // onFiltersLoaded: onFiltersLoaded
                }}
                search={{
                    searchPlaceholder: "Search",
                    onSearch: onSearch
                }}
            />
        )
    });
}
// #endregion
